/**
 * Turning a sentence into work: raising an order, and finding a window to do it in.
 *
 * This is where a chat assistant either becomes useful or becomes dangerous, so
 * the rule from the rest of the module is enforced hardest here. She reads the
 * instruction, fills in the form, shows the operator exactly what she filled in,
 * and stops. The raising is a button. Nothing in this file calls the platform's
 * write endpoints — it only builds the argument that a button will pass.
 *
 * The parsing is deliberately literal, for the same reason the reminder parser
 * is. An operator who says "P1" gets P1; an operator who says "urgent" is asked
 * what priority they mean rather than having one chosen for them. On an airfield
 * the cost of a confident wrong priority is not a mis-sorted list, and there is
 * no way for the operator to see that she guessed.
 *
 * What she will not do:
 *   - invent a priority. Unstated means P3, and she says that she defaulted.
 *   - invent a due date. A date she misread is worse than no date.
 *   - guess at a zone. "A2" is a zone; "the holding position" is not.
 */
import type { CreateWorkOrderInput, WorkOrderKind, WorkOrderPriority } from "../domain/workOrders";

/* ── raising a work order ─────────────────────────────────────────────── */

export interface WorkOrderRequest {
  input: Omit<CreateWorkOrderInput, "siteId">;
  /** What she decided rather than read, so the operator can see the difference. */
  assumed: string[];
}

const KIND_WORDS: [RegExp, WorkOrderKind][] = [
  /* First: "out of scope" is the contract's word, and "replace" below would otherwise claim it. */
  [/\b(out[- ]of[- ]scope|outside (the )?scope|not in (the )?contract)\b/i, "out_of_scope"],
  [/\b(inspect|inspection|check over|walk)\b/i, "inspection"],
  [/\b(preventive|preventative|routine|scheduled|planned|service)\b/i, "preventive"],
  [/\b(predictive|forecast|predicted)\b/i, "predictive"],
  [/\b(project|capital|capex|refurb|replace)\b/i, "project"],
];

/** "P1".."P5", and nothing looser: "urgent" is a feeling, not a priority. */
function priorityIn(q: string): WorkOrderPriority | null {
  const m = /\bp\s?([1-5])\b/i.exec(q);
  return m ? (Number(m[1]) as WorkOrderPriority) : null;
}

/**
 * Zone and asset codes the platform actually uses: A2, TWY-B, RWY 17/35, S2825.
 *
 * The priority is stripped first. "Raise a P2 work order to sweep A2" otherwise
 * yields P2 as the zone — a letter followed by a digit is exactly what both look
 * like, and the priority comes first in the sentence.
 */
function zoneIn(raw: string): string | null {
  const q = raw.replace(/\bp\s?[1-5]\b/gi, " ");
  const m =
    /\b(RWY[\s-]?\d{2}\/\d{2})\b/i.exec(q) ??
    /\b(TWY[\s-]?[A-Z]\d?)\b/i.exec(q) ??
    /\b(?:zone|position|stand|hold(?:ing)?)\s+([A-Z]\d{1,2})\b/i.exec(q) ??
    /\b([A-Z]\d{1,2})\b/.exec(q);
  return m ? m[1].toUpperCase().replace(/\s+/g, "-") : null;
}

export function isScheduleRequest(q: string): boolean {
  const t = q.toLowerCase().trim();
  return (
    /^(raise|create|open|log|schedule|book|add|new)\b.*\b(work order|wo|job|task)\b/.test(t) ||
    /^(raise|create|open|log)\s+(a\s+)?(new\s+)?(work order|wo)\b/.test(t) ||
    /\bschedule\b.*\b(sweep|repair|inspection|removal|replacement|service|maintenance)\b/.test(t)
  );
}

/**
 * Reads the instruction into a work order, and reports what it had to assume.
 *
 * Returns null when there is nothing left after the verbs — "raise a work
 * order" on its own is a request for a conversation, not an instruction.
 */
export function parseWorkOrder(q: string): WorkOrderRequest | null {
  const assumed: string[] = [];

  /* Strip the instruction verbs and the noun, leaving the substance. */
  let title = q
    .replace(/^\s*(please\s+)?(raise|create|open|log|schedule|book|add)\s+/i, "")
    .replace(/^(a|an|the)\s+/i, "")
    /* "New Work Order" is the button's name now, and people say what they see */
    .replace(/^new\s+/i, "")
    .replace(/\b(work order|wo|job|task)\b\s*(for|to|about|on)?\s*/i, "")
    .replace(/\bp\s?[1-5]\b/i, "")
    .trim()
    .replace(/\s{2,}/g, " ")
    .replace(/[.,;]+$/, "");

  if (title.length < 3) return null;
  title = title.charAt(0).toUpperCase() + title.slice(1);

  const priority = priorityIn(q);
  if (priority === null) assumed.push("Priority P3 — you did not say, and I will not pick one that matters.");

  const kind = KIND_WORDS.find(([re]) => re.test(q))?.[1] ?? "corrective";
  if (!KIND_WORDS.some(([re]) => re.test(q))) assumed.push("Kind: corrective, as the default.");

  const zoneCode = zoneIn(q) ?? undefined;
  if (!zoneCode) assumed.push("No zone — I only read codes like A2, TWY-B or RWY 17/35.");

  return {
    input: { title, kind, priority: priority ?? 3, zoneCode, description: `Raised from an instruction: “${q.trim()}”` },
    assumed,
  };
}

/**
 * The lines shown beneath the draft's title: what is about to be raised.
 *
 * The title is deliberately not repeated here — the card already carries it as
 * its heading, and printing it twice made the operator read the same sentence
 * to check one fact.
 */
export function describeWorkOrder(r: WorkOrderRequest): string[] {
  const lines = [`P${r.input.priority} · ${r.input.kind}${r.input.zoneCode ? ` · ${r.input.zoneCode}` : ""}`];
  return lines.concat(r.assumed.map((a) => `Assumed — ${a}`));
}

/* ── planning the work ────────────────────────────────────────────────── */

export interface PlanRequest {
  positionCode: string;
  category: string;
  hoursNeeded: number;
}

export function isPlanRequest(q: string): boolean {
  const t = q.toLowerCase().trim();
  return (
    /^when (can|could|should) (i|we)\b/.test(t) ||
    /^(plan|find a window|find me a window|when is there)\b/.test(t) ||
    /\bbest time to\b/.test(t)
  );
}

/**
 * Reads a planning question, or says which piece is missing.
 *
 * The platform needs three things — where, what kind of work, and for how long
 * — and refuses to answer without them. Rather than guessing at any of them and
 * handing back a window for the wrong job, the missing ones come back by name so
 * she can ask a single specific question.
 */
export function parsePlan(q: string): { plan: PlanRequest } | { missing: string[] } {
  const missing: string[] = [];

  const position = zoneIn(q);
  if (!position) missing.push("which position — a code like RWY 17/35, TWY-B or A2");

  const hours = /(\d{1,2}(?:\.\d)?)\s*(?:h|hr|hrs|hour|hours)\b/i.exec(q);
  if (!hours) missing.push("how many hours the work needs");

  /* The platform takes the category as free text, so the useful thing is to
     lift the work itself out of the sentence rather than match a list. */
  const cat =
    /\b(rubber removal|friction test(?:ing)?|sweep(?:ing)?|paint(?:ing)?|line marking|lighting|resurfacing|crack seal(?:ing)?|inspection|drainage|grass cutting)\b/i.exec(
      q,
    );
  if (!cat) missing.push("what work it is — rubber removal, friction testing, sweeping, lighting and so on");

  if (missing.length > 0) return { missing };
  return {
    plan: {
      positionCode: position!,
      category: cat![1].toLowerCase(),
      hoursNeeded: Number(hours![1]),
    },
  };
}
